import React, { useMemo } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import PropTypes from 'prop-types';

import { useConciergeContextState } from 'src/components/concierge/ConciergeContext';
import { getGeoAnalytics } from 'src/Query/warehouse';
import { TDSCountMarker } from 'src/components/project/explore/charts/RegionCountsMap';
import useDebouncedQuery from 'src/hooks/useDebouncedQuery';
import useMap from './hooks/useMap';
import { MapContext } from './MapContext';

const ZOOM_LEVEL_TDS = 'state';
const ZOOM_LEVEL_COUNTRY = 'country';
const ZOOM_LEVEL_REGION = 'region';

export default function MapPageWarehouse({
  additionalMarkers,
  useMapProps,
  ...props
}) {
  const { map, mapContainer, zoom } = useMap({
    mapProps: {
      zoom: 1 // Start zoomed out
    },
    ...(useMapProps || {})
  });

  const zoomLevel =
    (zoom < 3 && ZOOM_LEVEL_REGION) ||
    (zoom < 5 && ZOOM_LEVEL_COUNTRY) ||
    ZOOM_LEVEL_TDS;

  const nextZoomLevel = (level) => {
    if (level < 3) return 3;
    if (level < 5) return 5;
    return level + 1;
  };

  const handleMarkerClick = (m, lat, lng) => {
    m.flyTo({
      center: [lng, lat],
      zoom: nextZoomLevel(m.getZoom()),
      speed: 0.5
    });
  };

  const [filters] = useConciergeContextState(['explore', 'filters']);
  const completeFilters = { ...filters, level: zoomLevel };

  const { data } = useDebouncedQuery(
    ['warehouse', 'geo-analytics', completeFilters],
    () => getGeoAnalytics(completeFilters)
  );

  const markers = useMemo(
    /* eslint-disable react/no-array-index-key */
    () => {
      if (!map || !data) return additionalMarkers || [];

      const allMarkers = [
        ...Object.keys(data).map((key) => (
          <TDSCountMarker
            supplier={data[key]}
            id={key}
            key={key}
            map={map}
            handleMarkerClick={handleMarkerClick}
            value={data[key].tdss}
            subvalue={data[key].suppliers}
          />
        )),
        ...(additionalMarkers || [])
      ];

      return allMarkers;
    },
    [map, data, additionalMarkers]
    /* eslint-enable react/no-array-index-key */
  );

  return (
    <MapContext.Provider value={map}>
      <div ref={mapContainer} className="risk-map" {...props}>
        {markers}
      </div>
    </MapContext.Provider>
  );
}

MapPageWarehouse.propTypes = {
  additionalMarkers: PropTypes.array,
  useMapProps: PropTypes.object
};
